// The third-party creative review page, `/creative-review#…`. Public: no
// session, no loader (the credential is in the fragment, which never reaches the
// router), and every mutation declares `noDataEffect`.
//
// One review link names one creative and lives 72 hours from its creation. Any
// number of people may open it; each confirms their email once, through a link
// emailed to them, and this browser then keeps an access token for that package
// (`lib/reviewer-access.ts`) until the link expires, so a reload, a bookmark or
// the emailed link opened again reopens the creative without another email.
//
// The URL keeps `#p=<share token>[&c=<creative id>]` through every state. The
// share token is the only credential that ever appears in it; the access token
// and the device id travel only in the JSON bodies of the public POSTs.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '@/api/client';
import { ApiError } from '@/api/errors';
import { useRegisteredMutation } from '@/api/mutation';
import { publicReview, publicReviewPackage } from '@/api/resources';
import type { PublicReviewPackageInput } from '@/api/resources';
import { frameBox, frameScale, usePreviewWidth } from '@/components/meta-preview-frame';
import { DisplayAnimationNote } from '@/components/display-animation-note';
import { displaySizeFor, formatDisplayKb } from '@/lib/media-validation/display';
import type {
  CreativeReviewPackageItem,
  DisplayReviewBanner,
  DisplayReviewBundle,
  PublicReviewPackage,
  ReviewVerifyCompleteResponse,
} from '@/api/types';
import type {
  CreativeApprovalRequest,
  CreativeApprovalVerdict,
  CreativeReviewDecisionRequest,
  CreativeReviewDestination,
  MetaPreviewPlacement,
  ReviewVerifyCompleteRequest,
  ReviewVerifyStartRequest,
} from '@/api/types.gen';
import { buildFragment, parseFragment, type ReviewFragment } from '@/lib/creative-review-fragment';
import {
  clearAccess,
  findAccessByShareToken,
  onAccessChange,
  pruneExpiredAccess,
  readAccess,
  reviewerDevice,
  saveAccess,
  shareKey,
} from '@/lib/reviewer-access';

/** One message for every way a link can be dead; the server answers all of them with the same 404. */
export const DEAD_LINK_MESSAGE =
  'This review link is no longer valid — it may have expired. Ask the person who sent it for a new link.';
/** A confirmation emailed before links carried the share token, once it no longer opens anything. */
export const LEGACY_CONFIRMATION_MESSAGE =
  'This confirmation link no longer opens the review. Open the original review link to request a new email.';
export const VIEW_ONLY_MESSAGE =
  "You're viewing through someone else's confirmation email. To record your own decision, confirm your email.";

const isNotFound = (error: unknown) => error instanceof ApiError && error.status === 404;

/**
 * The server's own sentence, without the `conflict: ` prefix every Conflict
 * carries on the wire. A 404 is never shown raw: it means the access or the
 * link has run out, and says so the way the rest of the page does.
 */
function serverMessage(error: unknown, fallback: string): string {
  if (isNotFound(error)) return DEAD_LINK_MESSAGE;
  const message = error instanceof Error ? error.message : '';
  return message.replace(/^conflict:\s*/i, '') || fallback;
}

function discriminatorOf(token: string | null, packageId: string | null): string {
  return token ? `s:${shareKey(token)}` : `p:${packageId ?? ''}`;
}

/** Rewrite the fragment in place: no history entry, no `hashchange`, no reload. */
function writeFragment(hash: string): void {
  if (window.location.hash === hash) return;
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}${hash}`,
  );
}

export function CreativeReviewScreen() {
  const [fragment, setFragment] = useState<ReviewFragment>(() => parseFragment(window.location.hash));

  useEffect(() => {
    pruneExpiredAccess();
    // Editing the address bar by hand, or opening another review link in this
    // tab, changes only the fragment: no navigation, so listen for it.
    const onHash = () => setFragment(parseFragment(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const replaceFragment = useCallback((hash: string) => {
    writeFragment(hash);
    setFragment(parseFragment(hash));
  }, []);

  if (fragment.kind === 'invalid') {
    return <PublicState text="This review link is malformed." error />;
  }
  if (fragment.kind === 'legacy') return <LegacyReview token={fragment.token} />;
  if (fragment.kind === 'verify') {
    return (
      <VerifyArrival
        key={fragment.challenge}
        challenge={fragment.challenge}
        token={fragment.token}
        creativeId={fragment.creativeId}
        onOpened={replaceFragment}
      />
    );
  }
  return (
    <PackageReview
      key={fragment.token}
      token={fragment.token}
      packageId={null}
      creativeId={fragment.creativeId}
    />
  );
}

// ── Arrival from the emailed confirmation link ───────────────────────

/**
 * Opens the creative directly, with no button and no extra click:
 * `verify/complete` is called once on load. The emailed link works any number
 * of times, in any browser, until the review link expires: every click gets the
 * same access, and it replaces whatever this browser held for the link. Only
 * when the confirmation fails does access already stored for the link open it.
 * Then the URL becomes `#p=<token>[&c=…]`, so a reload never needs the email.
 *
 * A confirmation emailed before links carried `p` keeps its `#v=` URL, since
 * there is no share token to rewrite it to.
 */
function VerifyArrival({
  challenge,
  token,
  creativeId,
  onOpened,
}: {
  challenge: string;
  token: string | null;
  creativeId: string | null;
  onOpened: (hash: string) => void;
}) {
  const queryClient = useQueryClient();
  // React StrictMode runs effects twice in development; one confirmation per
  // mount is enough, and a second would count as a reopen for staff.
  const started = useRef(false);
  const [state, setState] = useState<
    | { kind: 'confirming' }
    | { kind: 'open'; packageId: string }
    | { kind: 'failed'; notFound: boolean }
  >({ kind: 'confirming' });

  const confirm = useCallback(() => {
    setState({ kind: 'confirming' });
    const device = reviewerDevice();
    const body: ReviewVerifyCompleteRequest = { challenge, token, device };
    api
      .post<ReviewVerifyCompleteResponse>('/public/creative-reviews/verify/complete', { body })
      .then((response) => {
        const packageId = response.package.package_id;
        saveAccess(packageId, {
          token: response.access_token,
          expires_at: response.access_expires_at,
          shareToken: token,
        });
        const input: PublicReviewPackageInput = {
          discriminator: discriminatorOf(token, packageId),
          token,
          access: response.access_token,
          device,
        };
        // The confirmation already returned the package; the page shows it
        // without asking again.
        queryClient.setQueryData(publicReviewPackage.key(input), response.package);
        if (token) onOpened(buildFragment({ token, creativeId }));
        else setState({ kind: 'open', packageId });
      })
      .catch((error: unknown) => {
        // A confirmation that cannot be read right now, or one that has died,
        // takes nothing away from access this browser already holds for the
        // same link; the package read decides whether that is still live.
        if (token && findAccessByShareToken(token)) {
          onOpened(buildFragment({ token, creativeId }));
          return;
        }
        setState({ kind: 'failed', notFound: isNotFound(error) });
      });
  }, [challenge, token, creativeId, onOpened, queryClient]);

  // Always confirmed, even when this browser already holds access for the link:
  // the link clicked is the one that counts. The access held may be another
  // confirmation's (a colleague's forwarded email, which may only view), and
  // only a click that reaches the server can make this browser the one that
  // first opened its own email, and so one of the two that may decide.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    confirm();
  }, [confirm]);

  if (state.kind === 'open') {
    return <PackageReview token={null} packageId={state.packageId} creativeId={creativeId} />;
  }
  if (state.kind === 'failed') {
    if (state.notFound) {
      return <PublicState error text={token ? DEAD_LINK_MESSAGE : LEGACY_CONFIRMATION_MESSAGE} />;
    }
    return (
      <PublicState error text="Your email could not be confirmed right now.">
        <button className="btn btn-sm" style={{marginTop:10}} onClick={confirm}>Try again</button>
      </PublicState>
    );
  }
  return <PublicState text="Opening the review…" />;
}

// ── The review package ───────────────────────────────────────────────

/**
 * The package behind a review link (`token`) or, for a confirmation emailed
 * before links carried one, behind its package id.
 *
 * Without stored access the page still asks once with no `access`; since the
 * `crv` cookie fallback was removed (2026-10-08) that is always the uniform 404,
 * which means "not confirmed in this browser" and shows the gate.
 * With stored access, a 404 means the link has died (access is stored only
 * until its own expiry, and pruned past it), so the entry is cleared and the
 * page says so.
 */
function PackageReview({
  token,
  packageId,
  creativeId,
}: {
  token: string | null;
  packageId: string | null;
  creativeId: string | null;
}) {
  const queryClient = useQueryClient();
  const device = useMemo(reviewerDevice, []);
  const lookup = useCallback(() => {
    if (token) return findAccessByShareToken(token);
    const access = packageId ? readAccess(packageId) : null;
    return access && packageId ? { ...access, packageId } : null;
  }, [token, packageId]);
  const [stored, setStored] = useState(lookup);
  useEffect(() => onAccessChange(() => setStored(lookup())), [lookup]);

  const [dead, setDead] = useState(false);
  const [sent, setSent] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const access = stored?.token ?? null;
  const discriminator = discriminatorOf(token, packageId ?? stored?.packageId ?? null);
  const input: PublicReviewPackageInput = { discriminator, token, access, device };
  const pkg = useQuery(publicReviewPackage(input, { enabled: !dead }));

  // The access is not in the key, so a change of access — confirmed in another
  // tab, cleared after a 404 or before a new email — starts the read over. An
  // error from before the change says nothing about the new access.
  const askedWith = useRef(access);
  const accessSince = useRef(0);
  useEffect(() => {
    if (askedWith.current === access) return;
    askedWith.current = access;
    accessSince.current = Date.now();
    void queryClient.resetQueries({
      queryKey: publicReviewPackage.key({ discriminator, token: null, access: null, device }),
      exact: true,
    });
  }, [access, discriminator, device, queryClient]);

  useEffect(() => {
    if (!pkg.isError || !isNotFound(pkg.error) || !stored) return;
    if (pkg.errorUpdatedAt <= accessSince.current) return;
    setDead(true);
    clearAccess(stored.packageId);
  }, [pkg.isError, pkg.error, pkg.errorUpdatedAt, stored]);

  // Keep the address canonical, so a reload or a bookmark reopens this creative.
  useEffect(() => {
    if (token) writeFragment(buildFragment({ token, creativeId }));
  }, [token, creativeId]);

  if (dead) {
    return <PublicState error text={token ? DEAD_LINK_MESSAGE : LEGACY_CONFIRMATION_MESSAGE} />;
  }

  const gate = token
    ? (
        <EmailGate
          token={token}
          creativeId={creativeId}
          device={device}
          onBack={pkg.data ? () => setConfirming(false) : null}
          onSent={() => {
            // A new confirmation replaces whatever this browser held for the
            // link, including access it may only view with.
            if (stored) clearAccess(stored.packageId);
            setConfirming(false);
            setSent(true);
          }}
          onDead={() => setDead(true)}
        />
      )
    : null;

  if (confirming && gate) return gate;
  if (pkg.data) {
    return (
      <PackageBody
        pkg={pkg.data}
        creativeId={creativeId}
        access={access}
        device={device}
        onConfirm={token ? () => setConfirming(true) : null}
        onChanged={() => void pkg.refetch()}
      />
    );
  }
  if (pkg.isPending) return <PublicState text="Loading…" />;
  if (isNotFound(pkg.error) || (pkg.error instanceof ApiError && pkg.error.status === 401)) {
    if (!gate) return <PublicState error text={LEGACY_CONFIRMATION_MESSAGE} />;
    return sent ? <CheckYourInbox /> : gate;
  }
  return (
    <PublicState error text="This review could not be loaded right now.">
      <button className="btn btn-sm" style={{marginTop:10}} onClick={() => void pkg.refetch()}>
        Try again
      </button>
    </PublicState>
  );
}

function EmailGate({
  token,
  creativeId,
  device,
  onSent,
  onDead,
  onBack,
}: {
  token: string;
  creativeId: string | null;
  device: string;
  onSent: () => void;
  onDead: () => void;
  onBack: (() => void) | null;
}) {
  const [email, setEmail] = useState('');
  const start = useRegisteredMutation({
    noDataEffect: true,
    mutationFn: (_: void) => {
      const body: ReviewVerifyStartRequest = { token, email, creative_id: creativeId, device };
      return api.post<void>('/public/creative-reviews/verify/start', { body });
    },
    onSuccess: onSent,
    onError: (error) => {
      if (isNotFound(error)) onDead();
    },
  });
  const ready = /.+@.+\..+/.test(email.trim());

  return (
    <main className="public-creative-review">
      <header>
        <div className="meta-policy-eyebrow">Creative review</div>
        <h1>Confirm your email</h1>
        <p>
          We'll email you a link to confirm it's you. Your address, the time and your decision
          are recorded against whatever you approve.
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (ready && !start.isPending) void start.mutateAsync().catch(() => {});
          }}
        >
          <label>
            Your email
            <input
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@agency.com"
            />
          </label>
          <div className="flex gap-1" style={{marginTop:12}}>
            <button className="btn btn-primary" disabled={!ready || start.isPending}>
              {start.isPending ? 'Sending…' : 'Send me the link'}
            </button>
            {onBack && (
              <button type="button" className="btn btn-ghost" onClick={onBack}>
                Back to the review
              </button>
            )}
          </div>
        </form>
        {start.isError && !isNotFound(start.error) && (
          <div className="asset-err" style={{marginTop:12}}>
            {start.error?.message || 'That link could not be sent right now. Try again shortly.'}
          </div>
        )}
      </header>
    </main>
  );
}

function CheckYourInbox() {
  return (
    <main className="public-creative-review">
      <header>
        <div className="meta-policy-eyebrow">Creative review</div>
        <h1>Check your inbox</h1>
        <p>
          The link in the email opens this review. It keeps working until the review link
          expires, so you can use it again whenever you come back. If it doesn't arrive, check
          your spam folder, then reload this page to try again.
        </p>
      </header>
    </main>
  );
}

function PackageBody({
  pkg,
  creativeId,
  access,
  device,
  onConfirm,
  onChanged,
}: {
  pkg: PublicReviewPackage;
  creativeId: string | null;
  access: string | null;
  device: string;
  onConfirm: (() => void) | null;
  onChanged: () => void;
}) {
  // A legacy multi-creative package stacks its items; `c` names the one the
  // reviewer was looking at, so bring it into view once.
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current || !creativeId || pkg.items.length < 2) return;
    scrolled.current = true;
    document.getElementById(`review-item-${creativeId}`)?.scrollIntoView?.({ block: 'start' });
  }, [creativeId, pkg.items.length]);

  // A link names Meta previews or Display banners; a legacy multi-creative
  // package may in principle hold both.
  const display = pkg.items.filter((item) => item.display).length;
  const eyebrow = display === 0
    ? 'Official Meta previews'
    : display === pkg.items.length ? 'Display banners' : 'Creative review';

  return (
    <main className="public-creative-review">
      <header>
        <div className="meta-policy-eyebrow">{eyebrow}</div>
        <h1>{pkg.name}</h1>
        <p>
          Reviewing as <b>{pkg.verified_email}</b>. Each decision is recorded with your address
          and the time. You can change your mind — a later decision supersedes an earlier one,
          and both are kept.
        </p>
      </header>
      {pkg.items.map((item) => (
        <PackageItem
          key={item.creative_id}
          item={item}
          canDecide={pkg.can_decide}
          access={access}
          device={device}
          onConfirm={onConfirm}
          onChanged={onChanged}
        />
      ))}
    </main>
  );
}

function PackageItem({
  item,
  canDecide,
  access,
  device,
  onConfirm,
  onChanged,
}: {
  item: CreativeReviewPackageItem;
  canDecide: boolean;
  access: string | null;
  device: string;
  onConfirm: (() => void) | null;
  onChanged: () => void;
}) {
  const { frameRef, previewWidth } = usePreviewWidth();
  const [comment, setComment] = useState('');
  const decide = useRegisteredMutation({
    // No data effect: this page has no session, so a declared domain could
    // only send the coordinator to GET /data-revisions for a 401. The page
    // refetches its own public read through `onChanged`, and signed-in tabs
    // pick the approval up from the manifest.
    noDataEffect: true,
    mutationFn: (verdict: CreativeApprovalVerdict) => {
      const body: CreativeApprovalRequest = {
        creative_id: item.creative_id,
        verdict,
        comment: comment.trim() || null,
        access,
        device,
      };
      return api.post<void>('/public/creative-reviews/approvals', { body });
    },
    onSuccess: () => {
      setComment('');
      onChanged();
    },
    // A refusal can mean the page is out of date (this browser may only view,
    // or the creative changed), so read it again either way.
    onError: onChanged,
  });
  const locked = item.superseded || !canDecide || decide.isPending;

  return (
    <section id={`review-item-${item.creative_id}`} style={{marginBottom:32}}>
      <div className="meta-preview-card-head" style={{border:0,padding:'0 0 10px'}}>
        <div>
          <b>{item.creative_name}</b>
          {item.my_verdict && (
            <span className={`tag ${item.my_verdict === 'changes_requested' ? 'warn' : ''}`}>
              {item.my_verdict === 'approved' ? 'You approved this' : 'You requested changes'}
            </span>
          )}
        </div>
      </div>
      {item.superseded && (
        <div className="asset-err" style={{marginBottom:12}}>
          This creative changed after the link was shared. Ask for a new review link — decisions
          are refused until then.
        </div>
      )}
      {item.display && <DisplayBanners display={item.display} />}
      {item.previews && (
      <div className="review-previews" aria-busy={item.refreshing || undefined}>
        {item.refreshing && (
          <div className="review-refreshing-note" role="status">Refreshing previews…</div>
        )}
        <div className="meta-preview-grid">
          {item.previews.variants.map((variant) => (
            <article className="meta-preview-card" key={variant.placement}>
              <div className="meta-preview-card-head">
                <div><b>{variant.label}</b><span>{variant.ad_format}</span></div>
                <span className={`tag ${variant.status !== 'ready' ? 'warn' : ''}`}>{variant.status}</span>
              </div>
              <div className="meta-preview-frame" ref={frameRef} style={frameBox(variant, previewWidth)}>
                {variant.embed_url && variant.status === 'ready'
                  ? <iframe
                      src={variant.embed_url}
                      title={`${variant.label} Meta preview`}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      allow="autoplay; encrypted-media"
                      style={frameScale(variant, previewWidth)}
                    />
                  : <div className="meta-preview-placeholder">Preview unavailable</div>}
              </div>
            </article>
          ))}
        </div>
      </div>
      )}
      {!item.superseded && item.destination && <DestinationSection destination={item.destination} />}
      {/* Like the destination, the click-through is the creative's current
          one, so a superseded item's would not be what the link was built from. */}
      {!item.superseded && item.display && (
        <LandingSection
          url={item.display.click_through_url}
          eyebrow="Click-through"
          note="Every banner links here. Approving confirms this page too."
        />
      )}
      <div style={{marginTop:12,maxWidth:640}}>
        {!canDecide && !item.superseded && (
          <div className="meta-preview-banner" style={{marginBottom:10}}>
            <span>{VIEW_ONLY_MESSAGE}</span>
            {onConfirm && (
              <button className="btn btn-sm" onClick={onConfirm}>Confirm my email</button>
            )}
          </div>
        )}
        <textarea
          className="input"
          rows={2}
          maxLength={2000}
          disabled={!canDecide || item.superseded}
          placeholder={item.display
            ? 'Optional note — which banner, and what needs to change'
            : 'Optional note — which placement, and what needs to change'}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
        <div className="meta-preview-actions" style={{padding:'8px 0 0',border:0}}>
          <button
            className="btn btn-sm"
            disabled={locked}
            onClick={() => { void decide.mutateAsync('changes_requested').catch(() => {}); }}
          >
            Request changes
          </button>
          <button
            className="btn btn-sm btn-primary"
            disabled={locked}
            onClick={() => { void decide.mutateAsync('approved').catch(() => {}); }}
          >
            ✓ Approve
          </button>
        </div>
        {decide.isError && (
          <div className="asset-err" style={{marginTop:8}}>
            {serverMessage(decide.error, 'That decision could not be saved.')}
          </div>
        )}
      </div>
    </section>
  );
}

// ── The destination (show only) ──────────────────────────────────────

/** The host a URL or a bare display domain (`shubert.com/tickets`) names, lower-cased. */
function hostOf(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`);
    return url.hostname.replace(/\.$/, '') || null;
  } catch {
    return null;
  }
}

/**
 * The last two labels of a host: `tickets.shubert.com` → `shubert.com`.
 *
 * A heuristic, not the Public Suffix List, which is too large to ship for one
 * warning. It is wrong under multi-label suffixes: `a.co.uk` and `b.co.uk` both
 * read `co.uk`, so a mismatch there goes unflagged. It never flags a host
 * against its own subdomain. The warning is advice to a reviewer; it decides
 * nothing.
 */
function registrableDomain(host: string): string {
  return host.split('.').filter(Boolean).slice(-2).join('.');
}

function DestinationSection({ destination }: { destination: CreativeReviewDestination }) {
  return (
    <LandingSection
      url={destination.url}
      eyebrow="Destination"
      shown={destination}
      note="Approving confirms this destination too."
    />
  );
}

/**
 * Where an ad sends people, written out so a reviewer can read every part of
 * it: the host in bold, the query parameters listed, a warning when it is not
 * a web address at all, and (for Meta) when the domain the ad shows is not the
 * one it links to.
 */
function LandingSection({
  url: raw,
  eyebrow,
  shown = null,
  note,
}: {
  url: string;
  eyebrow: string;
  shown?: Pick<CreativeReviewDestination, 'display_url' | 'cta_label'> | null;
  note: string;
}) {
  let url: URL | null = null;
  try {
    url = new URL(raw);
  } catch {
    url = null;
  }
  const web = url !== null && (url.protocol === 'https:' || url.protocol === 'http:');
  const shownHost = shown ? hostOf(shown.display_url) : null;
  const mismatch = web && shownHost !== null
    && registrableDomain(shownHost) !== registrableDomain(url!.hostname.toLowerCase());
  const params = url ? [...url.searchParams.entries()] : [];

  return (
    <div className="review-destination">
      <div className="meta-policy-eyebrow">{eyebrow}</div>
      <div className="review-destination-url">
        {url
          ? <>{url.protocol}//<b>{url.host}</b>{url.pathname}{url.search}{url.hash}</>
          : raw || '—'}
      </div>
      {shown && (
        <div className="review-destination-shown">
          The ad shows <b>{shown.display_url || '—'}</b>
          {' · '}button <b>{shown.cta_label || '—'}</b>
        </div>
      )}
      {params.length > 0 && (
        <dl className="review-destination-params">
          {params.map(([key, value], index) => (
            <div key={`${key}-${index}`}>
              <dt>{key}</dt>
              <dd>{value || '—'}</dd>
            </div>
          ))}
        </dl>
      )}
      {!web && (
        <div className="asset-err">This destination is not a web address a browser can open.</div>
      )}
      {mismatch && (
        <div className="asset-err" role="note">
          The ad shows {shownHost}, but the link goes to {url!.hostname.toLowerCase()}. Check that
          this is the page you expect.
        </div>
      )}
      <div className="review-destination-foot">
        {web && (
          <a className="btn btn-sm" href={url!.href} target="_blank" rel="noopener noreferrer">
            Open destination
          </a>
        )}
        <span className="muted">{note}</span>
      </div>
    </div>
  );
}

// ── Display banners (show only) ──────────────────────────────────────

/**
 * Every banner at its own pixel size, as a publisher's page would show it: a
 * 970×250 is not shrunk to fit a phone, it scrolls sideways inside its frame,
 * so the reviewer judges the real thing. An animation that breaks The Trade
 * Desk's 15-second guidance is said beside it; it is not refused.
 */
function DisplayBanners({ display }: { display: DisplayReviewBundle }) {
  if (display.banners.length === 0) {
    return <div className="meta-preview-placeholder">This creative has no banners.</div>;
  }
  return (
    <div className="review-display-banners">
      {display.banners.map((banner) => (
        <DisplayBannerFigure key={banner.placement_tag} banner={banner} />
      ))}
    </div>
  );
}

function DisplayBannerFigure({ banner }: { banner: DisplayReviewBanner }) {
  const [failed, setFailed] = useState(false);
  const name = displaySizeFor(banner.width, banner.height)?.name;
  const size = `${banner.width}×${banner.height}`;
  const label = name ? `${name} banner, ${size}` : `Banner, ${size}`;
  return (
    <figure className="review-display-banner" data-placement={banner.placement_tag}>
      <figcaption className="review-display-banner-head">
        <b>{size}</b>
        {name && <span>{name}</span>}
        <span className="muted">{formatDisplayKb(Number(banner.byte_size))}</span>
      </figcaption>
      <div className="review-display-banner-frame">
        {banner.view_url && !failed
          ? <img
              src={banner.view_url}
              width={banner.width}
              height={banner.height}
              alt={label}
              referrerPolicy="no-referrer"
              onError={() => setFailed(true)}
            />
          : <div
              className="review-display-banner-missing"
              role="img"
              aria-label={`${label}: image unavailable`}
              style={{ width: banner.width, height: banner.height }}
            >
              Image unavailable. Reload the page to try again.
            </div>}
      </div>
      <DisplayAnimationNote
        animation={banner.exceeds_animation_limit ? banner.animation : null}
        exceeds={banner.exceeds_animation_limit}
        byteSize={banner.byte_size}
      />
    </figure>
  );
}

// ── Pre-verification single-creative links ───────────────────────────
//
// Unchanged. Links minted before verification existed stay valid for their full
// seven days, and pulling this out from under a reviewer mid-review would be a
// worse failure than carrying it.

function LegacyReview({ token }: { token: string }) {
  const { frameRef, previewWidth } = usePreviewWidth();
  const [reviewerName, setReviewerName] = useState('');
  const [comments, setComments] = useState<Record<string, string>>({});
  const [decisions, setDecisions] = useState<Record<string, string>>({});
  const review = useQuery(publicReview(token));
  const decide = useRegisteredMutation({
    noDataEffect: true,
    mutationFn: (input: CreativeReviewDecisionRequest) =>
      api.post<void>(
        '/public/creative-reviews/decisions',
        { body: { token, ...input } },
      ),
    onSuccess: (_result, input) => {
      setDecisions((current) => ({
        ...current,
        [input.placement]: input.decision,
      }));
    },
  });
  const refresh = useRegisteredMutation({
    noDataEffect: true,
    mutationFn: (_: void) =>
      api.post<void>('/public/creative-reviews/refresh', { body: { token } }),
    onSuccess: () => {
      window.setTimeout(() => void review.refetch(), 1_000);
    },
  });
  // Deliberately NOT automatic. This endpoint is unauthenticated, so a link
  // that leaks or gets crawled must not be able to drive Meta work just by
  // being opened. Regeneration stays an explicit click (previews only, never a
  // provider sync).
  const canRegenerate = !!review.data
    && !review.data.superseded
    && !review.data.previews.fresh;

  if (review.isPending) return <PublicState text="Loading official Meta previews…" />;
  if (review.isError || !review.data) {
    return <PublicState text="This review link is invalid, expired, or unavailable." error />;
  }

  const submit = (
    placement: MetaPreviewPlacement,
    decision: 'reviewed' | 'changes_requested',
  ) => {
    if (!reviewerName.trim()) return;
    // Rejections are rendered from decide.isError; swallow here so a 409 or a
    // rate-limit response does not surface as an unhandled rejection.
    void decide.mutateAsync({
      placement,
      decision,
      reviewer_name: reviewerName.trim(),
      comment: comments[placement]?.trim() || null,
    }).catch(() => {});
  };

  return (
    <main className="public-creative-review">
      <header>
        <div className="meta-policy-eyebrow">Official Meta previews</div>
        <h1>{review.data.creative_name}</h1>
        <p>Review every placement. Your response is pinned to this exact creative revision.</p>
        <label>
          Your name
          <input
            className="input"
            value={reviewerName}
            onChange={(event) => setReviewerName(event.target.value)}
            placeholder="Reviewer name"
          />
        </label>
        {review.data.superseded && (
          <div className="asset-err" style={{marginTop:12}}>
            This link is pinned to an older creative revision. Ask for a new review link.
          </div>
        )}
        {canRegenerate && (
          <div className="meta-preview-banner" style={{marginTop:12}}>
            These previews were rendered a while ago and may not display.{' '}
            <button
              className="btn btn-sm"
              disabled={refresh.isPending}
              onClick={() => { void refresh.mutateAsync().catch(() => {}); }}
            >
              {refresh.isPending ? 'Regenerating…' : 'Regenerate previews'}
            </button>
          </div>
        )}
        {refresh.isError && (
          <div className="asset-err" style={{marginTop:12}}>
            Previews could not be regenerated right now. Try again in a few minutes.
          </div>
        )}
        {decide.isError && (
          <div className="asset-err" style={{marginTop:12}}>
            {decide.error?.message || 'That response could not be saved.'}
          </div>
        )}
      </header>
      <div className="meta-preview-grid">
        {review.data.previews.variants.map((variant) => (
          <article className="meta-preview-card" key={variant.placement}>
            <div className="meta-preview-card-head">
              <div><b>{variant.label}</b><span>{variant.ad_format}</span></div>
              <span className={`tag ${variant.status !== 'ready' ? 'warn' : ''}`}>{variant.status}</span>
            </div>
            <div className="meta-preview-frame" ref={frameRef} style={frameBox(variant, previewWidth)}>
              {variant.embed_url && variant.status === 'ready'
                ? <iframe
                    src={variant.embed_url}
                    title={`${variant.label} Meta preview`}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    allow="autoplay; encrypted-media"
                    style={frameScale(variant, previewWidth)}
                  />
                : <div className="meta-preview-placeholder">Preview unavailable</div>}
            </div>
            <div style={{padding:10}}>
              {review.data.previews.external_feedback
                .filter((feedback) => feedback.placement === variant.placement)
                .map((feedback) => (
                  <div key={`${feedback.reviewer_name}-${feedback.decided_at}`} className="muted" style={{fontSize:10.5,marginBottom:6}}>
                    <b>{feedback.decision === 'reviewed' ? 'Reviewed' : 'Changes requested'}</b>
                    {' · '}{feedback.reviewer_name}{feedback.comment ? ` — ${feedback.comment}` : ''}
                  </div>
                ))}
              <textarea
                className="input"
                rows={2}
                placeholder="Optional comment"
                value={comments[variant.placement] || ''}
                onChange={(event) => setComments((current) => ({
                  ...current,
                  [variant.placement]: event.target.value,
                }))}
              />
              <div className="meta-preview-actions" style={{padding:'8px 0 0',border:0}}>
                <button
                  className={`btn btn-sm ${decisions[variant.placement] === 'changes_requested' ? 'btn-danger' : ''}`}
                  disabled={review.data.superseded || variant.status !== 'ready' || !reviewerName.trim() || decide.isPending}
                  onClick={() => submit(variant.placement, 'changes_requested')}
                >
                  Request changes
                </button>
                <button
                  className={`btn btn-sm ${decisions[variant.placement] === 'reviewed' ? 'btn-primary' : ''}`}
                  disabled={review.data.superseded || variant.status !== 'ready' || !reviewerName.trim() || decide.isPending}
                  onClick={() => submit(variant.placement, 'reviewed')}
                >
                  ✓ Reviewed
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}

function PublicState({
  text,
  error = false,
  children,
}: {
  text: string;
  error?: boolean;
  children?: ReactNode;
}) {
  // Every state of the page names it: a malformed, dead or loading link used to render only
  // the message, so the page had no heading at all (crawl backlog B14).
  return (
    <main className="public-creative-review">
      <header>
        <h1>Creative review</h1>
      </header>
      <div className="facet" style={{color:error ? 'var(--bad)' : 'var(--ink-2)'}}>{text}</div>
      {children}
    </main>
  );
}
